import { useEffect, useState } from "react"

import { api, type Conta } from "../api"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../componentes/DropdownMenu"
import * as Icones from "../componentes/Icones"
import { BOTAO_DA_CASCA } from "../lib/classes"
import { cn } from "../lib/cn"
import { APARENCIA_TROCADA, CORES, NOME_DA_COR, aparenciaGuardada, mudarAparencia, type Aparencia, type Cor } from "../preferencias"
import type { AbaDoPerfil } from "../telas/Perfil"

export const FOTO_DO_PERFIL_TROCADA = "foto-do-perfil-trocada"

const ITENS_DO_MENU: { aba: AbaDoPerfil; rotulo: string }[] = [
  { aba: "dados", rotulo: "Meus dados" },
  { aba: "designacoes", rotulo: "Designações" },
  { aba: "seguranca", rotulo: "Segurança" },
  { aba: "atividade", rotulo: "Atividade" },
  { aba: "preferencias", rotulo: "Preferências" },
]

export function perfisVisiveis(conta: Conta): { texto: string; dica: string } {
  if (conta.is_root) return { texto: "Root", dica: "Root" }
  const todos = conta.perfis_na_tela ?? []
  const principal = todos.find((p) => p.papel === conta.papel) ?? todos[0]
  if (principal === undefined) return { texto: conta.papel, dica: conta.papel }
  const ordenados = [principal, ...todos.filter((p) => p !== principal)]
  return {
    texto: ordenados.length > 1 ? `${principal.nome} +${ordenados.length - 1}` : principal.nome,
    dica: ordenados.map((p) => p.nome).join(", "),
  }
}

function useFotoDoPerfil(): string | null {
  const [endereco, definirEndereco] = useState<string | null>(null)
  const [versao, definirVersao] = useState(0)

  useEffect(() => {
    const aoTrocar = () => definirVersao((n) => n + 1)
    window.addEventListener(FOTO_DO_PERFIL_TROCADA, aoTrocar)
    return () => window.removeEventListener(FOTO_DO_PERFIL_TROCADA, aoTrocar)
  }, [])

  useEffect(() => {
    let vivo = true
    let criado: string | null = null
    api
      .perfil()
      .then((perfil) => (perfil.tem_foto ? api.minhaFoto() : null))
      .then((foto) => {
        if (!vivo) return
        criado = foto === null ? null : URL.createObjectURL(foto)
        definirEndereco(criado)
      })
      .catch(() => {
        if (vivo) definirEndereco(null)
      })
    return () => {
      vivo = false
      if (criado !== null) URL.revokeObjectURL(criado)
    }
  }, [versao])

  return endereco
}

function useAparencia(): Aparencia {
  const [aparencia, definirAparencia] = useState<Aparencia>(() => aparenciaGuardada())
  useEffect(() => {
    const aoTrocar = (evento: Event) => definirAparencia((evento as CustomEvent<Aparencia>).detail)
    window.addEventListener(APARENCIA_TROCADA, aoTrocar)
    return () => window.removeEventListener(APARENCIA_TROCADA, aoTrocar)
  }, [])
  return aparencia
}

function PaletaDeCores() {
  const aparencia = useAparencia()
  return (
    <>
      <DropdownMenuLabel className="pb-1 text-xs font-medium text-muted-foreground">Cor do sistema</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={aparencia.cor}
        onValueChange={(cor) => mudarAparencia({ cor: cor as Cor })}
        className="flex items-center gap-1 px-1.5 pb-1.5"
      >
        {CORES.map((cor) => (
          <DropdownMenuRadioItem
            key={cor}
            value={cor}
            aria-label={NOME_DA_COR[cor]}
            title={NOME_DA_COR[cor]}
            onSelect={(evento) => evento.preventDefault()}
            className="cursor-pointer rounded-sm p-1 pl-1 data-[state=checked]:ring-2 data-[state=checked]:ring-(--tinta) [&>span:first-child]:hidden"
          >
            <span
              data-slot="amostra-da-cor"
              data-cor={cor}
              data-tom={aparencia.tom}
              data-tema={aparencia.modo === "escuro" ? "escuro" : undefined}
              className="flex size-6 items-center justify-center rounded-sm bg-(--azul-600) text-(--branco)"
            >
              {aparencia.cor === cor && <Icones.Validada tamanho={14} />}
            </span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}

export function AreaDoUsuario({
  conta,
  aoAbrirPerfil,
}: {
  conta: Conta
  aoAbrirPerfil: (aba: AbaDoPerfil) => void
}) {
  const foto = useFotoDoPerfil()
  const perfil = perfisVisiveis(conta)
  const primeiroNome = conta.nome.trim().split(/\s+/)[0] ?? conta.nome

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Menu de ${conta.nome}`}
          className="group inline-flex min-w-0 cursor-pointer items-center gap-2 rounded-(--raio-2) border border-transparent bg-transparent px-1.5 py-1 text-left text-(--tinta) hover:border-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current in-data-[slot=faixa]:text-(--rail-tinta-forte)"
        >
          {foto !== null ? (
            <img
              src={foto}
              alt={`Foto de ${conta.nome}`}
              className="size-8 shrink-0 rounded-(--raio-controle) border border-(--borda-forte) bg-(--branco) object-cover in-data-[slot=faixa]:border-[rgb(255_255_255/0.28)] in-data-[slot=faixa]:bg-[rgb(255_255_255/0.12)]" // anti-ia-ok: o mesmo branco translúcido do BOTAO_DA_CASCA
            />
          ) : (
            <span
              data-slot="icone-de-pessoa"
              aria-hidden="true"
              className={cn(BOTAO_DA_CASCA, "shrink-0")}
            >
              <Icones.Pessoa />
            </span>
          )}
          <span className="flex min-w-0 flex-col leading-tight max-[767px]:hidden">
            <span className="truncate text-(length:--texto-sm) font-semibold">{primeiroNome}</span>
            <span
              className="truncate text-(length:--texto-xs) text-(--tinta-2) in-data-[slot=faixa]:text-(--azul-100)"
              title={perfil.dica}
            >
              {perfil.texto}
            </span>
          </span>
          <span
            data-slot="seta-da-conta"
            aria-hidden="true"
            className="flex shrink-0 text-(--tinta-2) group-data-[state=open]:rotate-180 in-data-[slot=faixa]:text-(--azul-100)"
          >
            <Icones.Aumentar tamanho={16} />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {ITENS_DO_MENU.map((item) => (
          <DropdownMenuItem key={item.aba} onSelect={() => aoAbrirPerfil(item.aba)}>
            {item.rotulo}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <PaletaDeCores />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
